import { z } from 'zod';
import { getDateInTimeZoneISO, isValidISODate } from '@/lib/dateUtils';

const emailLike = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const phoneLike = /(?:\+?\d[\s().-]*){7,}/;

export function parseReviewContact(value: string) {
  const contact = value.trim();
  if (z.string().email().max(120).safeParse(contact).success) {
    return { authorEmail: contact.toLowerCase(), authorPhone: null };
  }
  if (/^\+?[\d\s().-]{7,30}$/.test(contact)) {
    const digits = contact.replace(/\D/g, '');
    if (digits.length >= 7 && digits.length <= 15) {
      return { authorEmail: null, authorPhone: digits };
    }
  }
  return null;
}

// Compartido entre cliente y servidor. Los campos privados nunca van a analytics ni al DTO público.
export function publicReviewSchema(today = getDateInTimeZoneISO(new Date(), 'America/Bogota')) {
  return z.object({
    slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(150),
    rating: z.number().int().min(1, 'Elige una calificación.').max(5),
    authorDisplayName: z.string().trim().min(2, 'Escribe tu nombre público.').max(60)
      .refine(value => !emailLike.test(value) && !phoneLike.test(value), 'Usa un nombre sin teléfono ni correo.'),
    reviewText: z.string().trim().min(10, 'Cuéntanos tu experiencia con al menos 10 caracteres.').max(3000)
      .refine(value => !emailLike.test(value) && !phoneLike.test(value), 'Retira teléfonos y correos del texto público.'),
    tourDate: z.string().refine(isValidISODate, 'Elige una fecha válida.')
      .refine(value => value <= today, 'Solo puedes reseñar una experiencia que ya realizaste.'),
    contact: z.string().trim().max(120).refine(value => !!parseReviewContact(value), 'Escribe un correo o WhatsApp válido, con indicativo de país.'),
    consentTextPublic: z.literal(true, { error: 'Necesitamos tu autorización para publicar la reseña.' }),
    consentPhotoPublic: z.boolean().default(false),
    _hp: z.string().max(0).optional(),
  });
}

export type PublicReviewSubmission = z.infer<ReturnType<typeof publicReviewSchema>>;

export const reviewVerificationSchema = z.object({
  verifiedExperience: z.union([z.literal(true), z.literal('on')]),
  bookingReference: z.string().trim().min(3).max(200),
  verificationChannel: z.enum(['whatsapp', 'email', 'phone', 'in_person', 'bold', 'beds24', 'manual']),
  verifiedPhotos: z.union([z.literal(true), z.literal('on')]).optional(),
});

/** Los formularios y fetch propios siempre envían Origin; no se admite envío cross-site. */
export function isReviewRequestSameOrigin(request: Request): boolean {
  const origin = request.headers.get('origin');
  const target = new URL(request.url);
  const configured = process.env.NEXT_PUBLIC_BASE_URL;
  const allowed = configured ? new URL(configured).origin : target.origin;
  if (origin === allowed) return true;
  // Permite QA local únicamente cuando la propia petición llega a loopback.
  return ['localhost', '127.0.0.1'].includes(target.hostname) && origin === target.origin;
}
